<template>
    <div class="row g-4 mt-1" v-show="chartValue.length">
        <template v-for="(item) in chartValue" :key="item.index" >
            <div class="col-md-6" v-if="item.consumption > 0">
                <div class="card usage-card h-100">
                    <div class="card-header">
                        <h2 class="card-header-title">{{ item.description }}</h2>
                    </div>
                    <div class="card-body">
                        <div class="row align-items-center gx-0">
                            <div class="col">
                                <h6 class="text-uppercase text-body-secondary mb-2">{{ item.unit }}
                                </h6>
                                <span class="usage-metric">{{ formatNumber(item.consumption, { decimals: 1 }) }}</span>
                                <span class="usage-metric__total">/ {{ formatNumber(item.quantity) }}</span>
                                <div class="row align-items-center g-0 mt-2">
                                    <div class="col-auto">
                                        <span class="h5 me-3 mb-0"
                                            :class="item.consumption_percentage > 100 ? 'text-danger' : 'text-primary'">{{ formatNumber(item.consumption_percentage, { decimals: 1 }) }}%</span>
                                    </div>
                                    <div class="col">
                                        <div class="progress usage-progress me-4">
                                            <div class="progress-bar" role="progressbar"
                                                :class="item.consumption_percentage > 100 ? 'bg-danger' : 'bg-primary'"
                                                :style="{ width: barWidth(item.consumption_percentage) + '%' }"
                                                :aria-valuenow="item.consumption_percentage" aria-valuemin="0"
                                                aria-valuemax="100">
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-auto">
                                <div class="usage-icon" :class="{ 'usage-icon--danger': item.consumption_percentage > 100 }">
                                    <i class="fa fa-coins"></i>
                                </div>
                            </div>
                        </div>

                        <hr />
                        <div class="chart-wrapper">
                            <canvas :ref="`chart${type + item.name}`" :id="`chart${type + item.name}`" class="chart-canvas">
                            </canvas>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>

</template>
<script>
import {
    Chart,
    ArcElement,
    LineElement,
    BarElement,
    PointElement,
    BarController,
    BubbleController,
    DoughnutController,
    LineController,
    PieController,
    PolarAreaController,
    RadarController,
    ScatterController,
    CategoryScale,
    LinearScale,
    LogarithmicScale,
    RadialLinearScale,
    TimeScale,
    TimeSeriesScale,
    Decimation,
    Filler,
    Legend,
    Title,
    Tooltip,
    SubTitle
} from 'chart.js';
import { formatNumber } from '../../utils/commonFunction';
import { styleDataset, barScales, tooltipStyle, limitLinePlugin } from '../../utils/chartTheme';


Chart.register(
    ArcElement,
    LineElement,
    BarElement,
    PointElement,
    BarController,
    BubbleController,
    DoughnutController,
    LineController,
    PieController,
    PolarAreaController,
    RadarController,
    ScatterController,
    CategoryScale,
    LinearScale,
    LogarithmicScale,
    RadialLinearScale,
    TimeScale,
    TimeSeriesScale,
    Decimation,
    Filler,
    Legend,
    Title,
    Tooltip,
    SubTitle
);

export default {
    props: {
        chartValue: {
            type: Array,
            default: () => []
        },
        type: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            charts: [],
        }
    },
    watch: {
        chartValue(value) {
            this.charts.map((mychart)=>{if(mychart) mychart.destroy();})
            this.$nextTick(() => {
                this.drawCharts()
            });
        }
    },
    mounted() {
        // Draw on mount so the charts render when the component is (re)created with
        // data already present — e.g. after leaving the tab and returning. Without this,
        // the chartValue watcher never fires and the cards render empty.
        this.$nextTick(() => this.drawCharts());
    },
    methods: {
        formatNumber(value, options) {
            return formatNumber(value, options);
        },
        barWidth(percentage) {
            const pct = Number(percentage) || 0;
            return Math.max(0, Math.min(pct, 100));
        },
        drawCharts() {
            // Destroy any existing charts first so repeated draws (mount + watch) never
            // collide on a canvas that is "already in use".
            this.charts.forEach(c => { if (c && typeof c.destroy === 'function') c.destroy(); });
            this.charts = [];

            if (this.chartValue) {
                for (const usageData of this.chartValue) {
                    let datasets = []
                    if (usageData.sub_entitlements) {
                        for (const subEnt of usageData.sub_entitlements) {
                            // Stacked bars for sub-tenant usage if available
                            if (subEnt.tenant_usage) {
                                for (const tenantUsage of subEnt.tenant_usage) {
                                    datasets.push(styleDataset({
                                        label: tenantUsage.cid + " Usage",
                                        data: [{key: subEnt.name, cid: tenantUsage.cid, value: tenantUsage.consumption, usage: tenantUsage.usage}],
                                        parsing: {
                                            xAxisKey: 'key',
                                            yAxisKey: 'value'
                                        },
                                    }, datasets.length))
                                }
                            }
                        }
                    }
                    //Entitlement POOLED CHART
                    const pooledChart = this.$refs['chart' + this.type + usageData.name]
                    const pooledData = {
                        datasets: datasets,
                    };
                    const pooledConfig = {
                        type: 'bar',
                        data: pooledData,
                        options: {
                            plugins: {
                                legend: {
                                    display: false,
                                },
                                title: {
                                    display: false,
                                    text: 'Credits'
                                },
                                tooltip: tooltipStyle({
                                    afterLabel: function(context) {
                                        const usage = context.raw && context.raw.usage != null ? context.raw.usage : 0;
                                        return "Count: " + usage.toLocaleString();
                                    }
                                }),
                                limitLine: {
                                    label: 'Limit',
                                    limit: usageData.quantity,
                                }
                            },
                            responsive: true,
                            maintainAspectRatio: false,
                            layout: { padding: { top: 8 } },
                            scales: barScales()
                        },
                        plugins: [limitLinePlugin]
                    };
                    
                    if (pooledChart) {
                        this.charts.push(new Chart(pooledChart, pooledConfig));
                    }
                }

            }
        },

    },
    beforeUnmount(){
        this.charts.forEach(chart => {
            if (chart && typeof chart.destroy === 'function') {
                chart.destroy();
            }
        });
    }
}
</script>
<style scoped>
.chart-wrapper {
    position: relative;
    width: 100%;
    height: 300px;
}

.usage-card {
    border: 1px solid rgba(18, 38, 63, 0.06);
}

.usage-metric {
    font-size: 1.75rem;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.01em;
}

.usage-metric__total {
    font-size: 1.05rem;
    font-weight: 500;
    color: #95aac9;
    margin-left: 0.35rem;
}

.usage-progress {
    height: 8px;
    border-radius: 999px;
    background-color: rgba(44, 123, 229, 0.1);
}

.usage-progress .progress-bar {
    border-radius: 999px;
}

.usage-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    color: #2c7be5;
    background: rgba(44, 123, 229, 0.1);
    font-size: 1.15rem;
}

.usage-icon--danger {
    color: #e63757;
    background: rgba(230, 55, 87, 0.1);
}
</style>